import _ from 'lodash'
// MODIFIED: Import the 'isValid' helper from date-fns
import { format, isValid } from 'date-fns'

// --- ADDED THIS NEW, SAFE FUNCTION ---
/**
 * Safely formats a date string using date-fns, returning 'N/A' if the input is invalid.
 * @param {string | Date | null | undefined} dateInput The date value from the API.
 * @param {string} formatString The desired output format.
 * @returns {string} The formatted date or 'N/A'.
 */
function formatDateSafely(dateInput, formatString = 'dd.MM.yy') {
  // 1. Handle null, undefined, or empty string inputs
  if (!dateInput) {
    return 'N/A';
  }

  const date = new Date(dateInput);

  // 2. Use the 'isValid' helper from date-fns to check if the created date is valid
  if (!isValid(date)) {
    console.error(`Attempted to format an invalid date: "${dateInput}"`);
    return 'N/A';
  }

  // 3. Only if the date is valid, format it
  return format(date, formatString);
}


export default function formatOrder(order) {
  const formattedAddress = getOrderHistoryAddress(order)

  // Add points information to each line item
  const itemList = (_.get(order, 'itemList.item') || []).map(item => ({
    ...item,
    itemPoints: _.get(item, 'custcol_smi_mysmi_points.value')
  }))

  return {
    ...order,
    id: order.internalId,
    order_method:
      _.get(order, 'custbody_bbs_order_method_salesorders.value.name') ||
      'Netsuite',
    order_type: isManpack(order) ? 'Wearer Pack' : 'Stock',
    order_number: order.tranId,
    isManpack: isManpack(order),
    ref: 'N/A',
    customer_po: order.otherRefNum,

    // --- MODIFIED THESE LINES TO USE THE SAFE FUNCTION ---
    date: formatDateSafely(order.createdDate),
    shipping_date: formatDateSafely(order.shipDate),

    formattedAddress: formattedAddress,
    ordered_by:
      _.get(order, 'custbody_bbs_opportunity_contact.value.name', '').split(
        ':'
      )[2] || 'N/A',
    order_status: getOrderStatus(order),
    itemList: itemList,
    rejectionNotes: _.get(order, 'custbody_bbs_rejection_notes.value'),
    instructions: getOrderInstructions(order),
    qty_ordered: getTotalItems(order),
    shippingCost: order.shippingCost || order.altShippingCost,
    taxTotal: order.taxTotal || order.tax || order.vatTotal || order.vat || 0,
    total:
      parseFloat(order.subTotal || 0) +
      parseFloat(order.shippingCost || order.altShippingCost || 0) +
      parseFloat(order.taxTotal || order.tax || order.vatTotal || order.vat || 0),

    // Add points budget information
    hasPointsBudget: order.hasPointsBudget === true,
    totalPoints: order.totalPoints || 0
  }
}

// ... all your other helper functions (getOrderStatus, etc.) remain unchanged ...
// They are correct as they are.

function getOrderStatus(order) {
  const statusMapping = {
    'Pending Approval': 'Pending Approval',
    'Pending Fulfillment': 'In Process',  // American spelling (old orders)
    'Pending Fulfilment': 'In Process',   // British spelling (new orders)
    Cancelled: 'Cancelled',
    Closed: 'Closed',
    'Partially Fulfilled': 'Part Despatched',
    'Pending Billing/Partially Fulfilled': 'Part Despatched',
    'Pending Billing': 'Fully Despatched',
    'Fully Billed': 'Fully Despatched',
    Billed: 'Fully Despatched'
  }

  if (_.get(order, 'custbody_bbs_web_rejected.value')) {
    return 'Order rejected'
  } else if (_.get(order, 'custbody_bbs_pend_web_approval.value')) {
    return 'Pending Supervisor Approval'
  } else {
    return statusMapping[order.status]
  }
}

function getOrderInstructions(order) {
  // Pattern "contact: 343, mobile: 344"
  let contacts = _.get(order, 'custbody_bbs_picking_notes_so.value', '')
  contacts = contacts
    .split(',')
    .map(o => o.split(':'))
    .map(o => ({
      name: _.trim(o[0]),
      value: _.trim(o[1])
    }))

  const result = {
    notes: order.message,
    poNumber: order.otherRefNum,
    message: order.message,
    costCentre: _.get(order, 'custbody_bbs_contact_centre.value')
  }

  for (let contact of contacts) {
    result[contact.name] = contact.value
  }

  return result
}

function isManpack(order) {
  return (
    _.get(
      order,
      'custbody_bbs_salesorder_type.value.$attributes.internalId'
    ) === '1'
  )
}

function getOrderHistoryAddress(order) {
  const fields = ['addressee', 'addr1', 'addr2', 'city', 'zip']
  let details = {}
  
  console.log('getOrderHistoryAddress called for order:', {
    internalId: order.internalId,
    isManpack: isManpack(order),
    hasShippingAddress: !!order.shippingAddress,
    hasItemList: !!order.itemList?.item?.length,
    firstItemShipAddress: order.itemList?.item?.[0]?.shipAddress,
    hasShipGroupList: !!order.shipGroupList?.shipGroup
  })
  
  if (order.shippingAddress) {
    // Standard stock order with shippingAddress at root level
    details = order.shippingAddress
    console.log('Using shippingAddress:', details)
  } else if (isManpack(order) && order.itemList && order.itemList.item && order.itemList.item.length > 0) {
    // Wearerpack order - get address from first item's shipAddress and match with shipGroup
    const firstItem = order.itemList.item[0]
    console.log('Processing wearerpack order, firstItem:', {
      hasShipAddress: !!firstItem.shipAddress,
      shipAddress: firstItem.shipAddress
    })
    
    if (firstItem.shipAddress) {
      const shipAddressName = firstItem.shipAddress.name || ''
      const shipAddressId = firstItem.shipAddress.$attributes?.internalId || firstItem.shipAddress.internalId
      
      console.log('Found shipAddress:', { name: shipAddressName, id: shipAddressId })
      
      // Find matching shipGroup for more detailed address
      if (order.shipGroupList && order.shipGroupList.shipGroup) {
        const shipGroups = Array.isArray(order.shipGroupList.shipGroup) 
          ? order.shipGroupList.shipGroup 
          : [order.shipGroupList.shipGroup]
          
        console.log('Searching in shipGroups:', shipGroups.length, 'groups')
        
        const matchingGroup = shipGroups.find(
          group => group.destinationAddressRef?.$attributes?.internalId === shipAddressId
        )
        
        console.log('Found matching group:', !!matchingGroup, matchingGroup?.destinationAddress)
        
        if (matchingGroup && matchingGroup.destinationAddress) {
          // Parse the destination address string
          const addressString = matchingGroup.destinationAddress
          const postcodeMatch = addressString.match(/([A-Z]{1,2}\d{1,2}\s?\d[A-Z]{2})$/i)
          const postcode = postcodeMatch ? postcodeMatch[1] : ''
          const addressWithoutPostcode = postcode 
            ? addressString.replace(postcode, '').trim()
            : addressString
            
          details = {
            addressee: shipAddressName,
            addr1: addressWithoutPostcode,
            zip: postcode
          }
          console.log('Extracted address details:', details)
        } else {
          // Fallback to just the ship address name
          details = {
            addressee: shipAddressName
          }
          console.log('Using fallback address (name only):', details)
        }
      } else {
        // No shipGroupList, use just the name
        details = {
          addressee: shipAddressName
        }
        console.log('No shipGroupList, using name only:', details)
      }
    }
  } else if (order.shipGroupList && order.shipGroupList.shipGroup) {
    // Fallback for other order types with shipGroupList
    const shipGroups = Array.isArray(order.shipGroupList.shipGroup) 
      ? order.shipGroupList.shipGroup 
      : [order.shipGroupList.shipGroup]
      
    if (shipGroups.length > 1) {
      details = {
        addressee: 'Multiple delivery addresses'
      }
    } else if (shipGroups[0] && shipGroups[0].destinationAddress) {
      details = {
        addressee: shipGroups[0].destinationAddress
      }
    }
    console.log('Using shipGroupList fallback:', details)
  }

  const result = fields
    .map(field => details[field])
    .filter(o => o)
    .join(' ')
    
  console.log('Final address result:', result)
  return result
}

function getTotalItems(order) {
  let totalItem = null

  if (order.itemList) {
    totalItem = order.itemList.item.reduce(
      (total, item) => (total += parseInt(item.quantity)),
      0
    )
  }

  return totalItem
}